import { usePageTitle } from "@/hooks/usePageTitle";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { SocialLinks } from "@/components/SocialLinks";
import { Microscope, Dumbbell, Users, Apple, LineChart, Activity, ArrowRight, Check } from "lucide-react";

const ANDREA = "https://customer-assets-jai6qajn.emergentagent.net/job_inbody-powered/artifacts/rt3xmc5g_IMG_0260.jpg";

const ANDREA_GALLERY = [
  { src: "https://customer-assets-jai6qajn.emergentagent.net/job_inbody-powered/artifacts/r2y8cmh1_Image%201.jpeg", alt: "Andrea cycling the Col du Tourmalet in the Pyrenees", caption: "Cycling the French cols" },
  { src: "https://customer-assets-jai6qajn.emergentagent.net/job_inbody-powered/artifacts/s50xc7sx_Image%202.jpeg", alt: "Andrea finishing the London Marathon in 1989 and 1992", caption: "London Marathons '89 & '92" },
  { src: "https://customer-assets-jai6qajn.emergentagent.net/job_inbody-powered/artifacts/08zdwuh4_Image%203.jpeg", alt: "Andrea at the Col de Val Louron-Azet summit", caption: "Col de Val Louron-Azet" },
  { src: "https://customer-assets-jai6qajn.emergentagent.net/job_inbody-powered/artifacts/epolqieb_Image.jpeg", alt: "Andrea strength training in the gym", caption: "Strength training today" },
];

const benefits = [
  { icon: Microscope, title: "Precision vs. guesswork", text: "We replace generic, one-size-fits-all systems with your own biological data, so you can make informed decisions about your health." },
  { icon: Dumbbell, title: "Body composition vs. weight", text: "We focus on retaining lean muscle mass and improving body composition — not just chasing a lower number on the scale." },
  { icon: Users, title: "Professional access", text: "Enjoy access to the expertise of a qualified Nutritional Therapist who understands the science." },
  { icon: Apple, title: "Science-based nutrition", text: "Our approach is rooted in whole foods — never 'free foods' or ultra-processed shortcuts (UPFs)." },
  { icon: LineChart, title: "Data-driven accountability", text: "Using weekly BIA data, we monitor the physiological markers that matter." },
  { icon: Activity, title: "Lasting, metabolic success", text: "Using modern, evidence-based protocols which are proven to provide lasting results." },
];

export default function About() {
  usePageTitle("About Andrea Wood");
  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Navbar />

      {/* Intro */}
      <section className="relative">
        <div className="brand-blob bg-teal" style={{ width: 380, height: 380, top: -80, right: -60 }} />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-8 pt-16 pb-20 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-navy/70">About My Best Body Club</span>
            <h1 className="mt-5 font-heading text-4xl sm:text-5xl tracking-tight leading-[1.08] text-navy">
              Moving the conversation beyond weight alone.
            </h1>
            <p className="mt-6 text-lg text-[#57534E] leading-relaxed">
              The biggest difference between My Best Body Club and standard clubs is our use of
              Bioelectrical Impedance Analysis (BIA). Traditional clubs base everything on weight alone —
              completely ignoring the metabolic markers that actually determine health and longevity.
            </p>
            <p className="mt-4 text-[#57534E] leading-relaxed">
              We shift the focus to the bigger picture. It's far more motivational than a simple number,
              and it finally provides an answer for those frustrating "stayed the same" moments when the
              scales don't budge.
            </p>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="relative">
            <div className="rounded-[2rem] overflow-hidden shadow-2xl bg-white p-3">
              <img src={ANDREA} alt="Andrea Wood, Nutritional Therapist" className="w-full h-auto rounded-[1.5rem]" />
            </div>
            <div className="absolute -bottom-5 -right-5 bg-teal text-white rounded-2xl px-5 py-4 shadow-xl">
              <p className="font-heading text-lg leading-tight">Andrea Wood</p>
              <p className="text-xs text-white/80">Nutritional Therapist · Founder</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About Andrea Wood */}
      <section className="py-20 px-6 lg:px-8 bg-[#F5F5F4]">
        <div className="max-w-3xl mx-auto" data-testid="about-andrea-section">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-teal">Meet your therapist</span>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl tracking-tight text-navy">About Andrea Wood</h2>
          <div className="mt-5 flex items-center gap-4">
            <SocialLinks variant="light" />
            <span className="text-sm text-[#78716C]">Follow Andrea</span>
          </div>
          <p className="mt-6 text-[#57534E] leading-relaxed">
            I'm a Nutritional Therapist with a simple mission: to help you stop fighting your body and start
            fuelling it for the life you want to lead.
          </p>
          <p className="mt-4 text-[#57534E] leading-relaxed">
            I graduated with a Dip ION/FdSc from the Institute for Optimum Nutrition in 2009 and have since
            built 17 years of experience — both with my own private clients and, more recently, consulting for
            a company providing therapy services to health insurance policyholders. The sheer volume of
            consultations in that role, far exceeding what I'd see privately, has given me unique insight into
            the needs of women over 45 as they navigate hormonal change.
          </p>
          <p className="mt-4 text-[#57534E] leading-relaxed">
            For a long time, I lived the frustration of the "dieting" cycle. Like many of you, I struggled with
            hormonal weight shifts that seemed to defy every conventional rule I followed. Even as an endurance
            cyclist and runner the 'belly' would not shift!
          </p>
          <p className="mt-4 text-[#57534E] leading-relaxed">
            It was only when I moved away from "weight loss" and toward metabolic health—focusing on blood sugar
            balance and body composition—that I truly began to feel better.
          </p>
          <p className="mt-4 text-[#57534E] leading-relaxed">
            My journey from that struggle to understanding the physiology of the female body is exactly why I do what
            I do today. I know first hand that when your hormones and metabolic markers are aligned, everything
            changes—your energy, your confidence, and your outlook on life.
          </p>
          <h3 className="mt-10 font-heading text-2xl text-navy">My Approach</h3>
          <p className="mt-4 text-[#57534E] leading-relaxed">
            I'm a big believer in practicing what I preach. You'll often find me out running or cycling, not just
            for the physical benefits, but because I've experienced how movement and nutrition work in tandem to
            support longevity. I'm a firm believer that health shouldn't be about restriction; it should be about
            science-backed protocols and sustainable habits.
          </p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="andrea-gallery">
            {ANDREA_GALLERY.map((f) => (
              <figure key={f.src} className="group">
                <div className="rounded-2xl overflow-hidden shadow-md aspect-square">
                  <img src={f.src} alt={f.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <figcaption className="mt-2 text-xs text-[#78716C] text-center">{f.caption}</figcaption>
              </figure>
            ))}
          </div>
          <h3 className="mt-10 font-heading text-2xl text-navy">My Mission</h3>
          <p className="mt-4 text-[#57534E] leading-relaxed">
            I founded My Best Body Club™ because I saw a gap in the industry. Women over 45 deserve more than
            generic advice (most of it based on research conducted on MEN!), and misleading scale numbers. They
            deserve a community, real-world strategies, and a path to vitality that actually works.
          </p>
          <p className="mt-4 text-[#57534E] leading-relaxed">
            My qualifications provide the science, but my lived experience—and my passion for seeing you
            thrive—is the heart of everything I do. I'm here to cut through the noise, support your journey, and
            help you feel as vibrant as possible.
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-teal">Why MBBC works</span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl tracking-tight text-navy">The benefits of joining the club</h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => (
              <motion.div key={b.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="bg-white border border-[#E7E5E4] rounded-2xl p-7 shadow-sm">
                <div className="w-12 h-12 rounded-xl bg-teal/10 flex items-center justify-center text-teal"><b.icon className="w-6 h-6" /></div>
                <h3 className="mt-5 font-heading text-xl text-navy">{b.title}</h3>
                <p className="mt-2 text-sm text-[#57534E] leading-relaxed">{b.text}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-14 bg-navy rounded-2xl p-8 sm:p-10 text-white flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-heading text-2xl">Ready to know your body?</h3>
              <p className="mt-1 text-white/70">Join the Virtual membership today — Physical and Hybrid launch early 2027.</p>
            </div>
            <Link to="/join" className="inline-flex items-center gap-2 bg-teal text-white rounded-full px-7 py-3.5 font-medium hover:bg-teal-dark transition-colors shrink-0">
              Join the club <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
